import type { AsyncStatus, Response } from 'lib-shared-legacy-types';
import { Action, handleActions } from 'redux-actions';

import type { NodeDataT, NodeT } from 'fable/flowEditor';

import { FlowNodeTypes } from '../../powerFlowShared/components/types';
import { getRequestID } from '../../shared/util/request';
import { emailFlowActions } from '../actions/emailFlowActions';
import type {
  FlowEmailActionT,
  FlowEmailAddNodePayloadT,
  FlowEmailGetPayloadT,
} from '../api/types';

type FlowResponseT = Response<NodeDataT<FlowEmailActionT[]>>;

type FlowEmailHandleActionsPayloadT =
  | FlowEmailGetPayloadT
  | FlowResponseT
  | FlowEmailAddNodePayloadT
  | any;

export type FlowEmailStateT = {
  id: string | null;
  current_flow: NodeDataT<FlowEmailActionT[]> | null;
  current_payload: Record<
    string,
    string | Record<string, string | []> | []
  > | null;
  email_address?: string | null;
  email_id?: string;
  meta: {
    asyncStatus: AsyncStatus;
    hasUnSavedChanges: boolean;
  };
  name: null | string;
  nodes: FlowEmailActionT[] | null;
};

const {
  emailFlowGet,
  emailFlowGetSucceeded,
  emailFlowGetFailed,
  emailFlowAdd,
  emailFlowAddSucceeded,
  emailFlowAddFailed,
  emailFlowEdit,
  emailFlowEditSucceeded,
  emailFlowEditFailed,
  emailFlowAddNode,
  emailFlowResetState,
} = emailFlowActions;

function emptyNode(): NodeT<FlowNodeTypes.ACTION> {
  return {
    badges: [],
    childIds: [],
    connectionLabel: 'arrow',
    icon: '',
    id: getRequestID(),
    isAction: true,
    isEditable: false,
    isRemovable: false,
    nodeType: FlowNodeTypes.ACTION,
    overlay: [],
    pageX: 100,
    pageY: 100,
    parentId: null,
    payload: null,
    text: '',
  };
}

const initialState: FlowEmailStateT = {
  id: null,
  meta: {
    asyncStatus: 'succeeded',
    hasUnSavedChanges: false,
  },
  email_address: null,
  current_flow: null,
  current_payload: null,
  name: null,
  nodes: [],
};

// Reducer
const emailFlow = handleActions<
  FlowEmailStateT,
  FlowEmailHandleActionsPayloadT
>(
  {
    [emailFlowAddNode.toString()]: (
      state,
      action: Action<FlowEmailAddNodePayloadT>,
    ) => {
      const { node } = action.payload;

      if (!node) {
        return state;
      }

      const newEmptyNode = emptyNode();

      const newNode = Object.assign({}, newEmptyNode, node);

      return { ...state, nodes: [...(state.nodes ?? []), newNode] };
    },

    [emailFlowGet.toString()]: (
      state,
      action: Action<FlowEmailGetPayloadT>,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'loading' },
        email_address: action.payload.email_address,
      };
    },

    [emailFlowGetSucceeded.toString()]: (
      state,
      action: Action<FlowResponseT>,
    ) => {
      return {
        ...state,
        id: action.payload.data.id,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        email_address: action.payload.data.location,
        name: action.payload.data.name,
        isActive: action.payload.data.active,
        nodes: action.payload.data.actions,
      };
    },

    [emailFlowGetFailed.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [emailFlowAdd.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [emailFlowAddSucceeded.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'succeeded' } };
    },

    [emailFlowAddFailed.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [emailFlowEdit.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [emailFlowEditSucceeded.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'succeeded' } };
    },

    [emailFlowEditFailed.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [emailFlowResetState.toString()]: (): FlowEmailStateT => initialState,
  },
  initialState,
);

export default emailFlow;
